import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Onboarding.css';

// Storage key prefix for tracking if user has seen onboarding (per-user)
const ONBOARDING_KEY_PREFIX = 'allocq_onboarding_completed_';

// Onboarding steps configuration
const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to AllocQ!',
    message: 'Let us show you how to optimize your mutual fund portfolio in just a few steps.',
    target: null, // No target - centered modal
    position: 'center'
  },
  {
    id: 'stepper',
    title: 'Your Progress',
    message: 'Follow these 4 steps to build and backtest your optimal portfolio. Click on any completed step to revisit it.',
    target: '.stepper',
    position: 'bottom'
  },
  {
    id: 'fund-selection',
    title: 'Select Your Funds',
    message: 'Search for mutual funds or scan a document to add funds to your portfolio. You need at least 2 funds to continue.',
    target: '.section',
    position: 'center-with-highlight'
  },
  {
    id: 'logo',
    title: 'Start Over Anytime',
    message: 'Click the AllocQ logo to reset and start a fresh portfolio analysis.',
    target: '.header-brand',
    position: 'bottom-right'
  },
  {
    id: 'chatbot',
    title: 'Meet Q — Your Portfolio Assistant',
    message: 'Tap the Q icon anytime to chat with your portfolio assistant. Q can explain your metrics in simple language, identify diversification gaps, and guide you through every step.',
    target: '.chat-fab',
    position: 'left'
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    message: 'Start by selecting your mutual funds. Our AI will help you find the optimal allocation for your goals. And remember — Q is always a tap away if you need help!',
    target: null,
    position: 'center'
  }
];

const Onboarding = ({ onComplete, userEmail }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const tooltipRef = React.useRef(null);

  const step = ONBOARDING_STEPS[currentStep];
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  // Calculate tooltip position based on target element
  const calculatePosition = useCallback(() => {
    // Center positions (with or without highlight)
    if (!step.target || step.position === 'center' || step.position === 'center-with-highlight') {
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      });
      setArrowStyle({ display: 'none' });
      return;
    }

    const targetElement = document.querySelector(step.target);
    if (!targetElement) {
      // Fallback to center if target not found
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      });
      setArrowStyle({ display: 'none' });
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const tooltipWidth = 340;
    // Measure actual tooltip height if available, fallback to estimate
    const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 220;
    const padding = 16;
    const arrowSize = 12;

    let top, left, arrowTop, arrowLeft, arrowDirection;

    switch (step.position) {
      case 'bottom':
        top = rect.bottom + padding + arrowSize;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowTop = -arrowSize;
        arrowLeft = tooltipWidth / 2 - arrowSize;
        arrowDirection = 'up';
        break;
      case 'bottom-right':
        top = rect.bottom + padding + arrowSize;
        left = rect.right - tooltipWidth;
        arrowTop = -arrowSize;
        arrowLeft = tooltipWidth - rect.width / 2 - arrowSize;
        arrowDirection = 'up';
        break;
      case 'top':
        top = rect.top - tooltipHeight - padding - arrowSize;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowTop = tooltipHeight;
        arrowLeft = tooltipWidth / 2 - arrowSize;
        arrowDirection = 'down';
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - padding - arrowSize;
        arrowTop = tooltipHeight / 2 - arrowSize;
        arrowLeft = tooltipWidth;
        arrowDirection = 'right';
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + padding + arrowSize;
        arrowTop = tooltipHeight / 2 - arrowSize;
        arrowLeft = -arrowSize;
        arrowDirection = 'left';
        break;
      default:
        top = rect.bottom + padding;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        arrowTop = -arrowSize;
        arrowLeft = tooltipWidth / 2 - arrowSize;
        arrowDirection = 'up';
    }

    // Keep tooltip within viewport with proper clamping
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left < padding) left = padding;
    if (left + tooltipWidth > viewportWidth - padding) {
      left = viewportWidth - tooltipWidth - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipHeight > viewportHeight - padding) {
      top = viewportHeight - tooltipHeight - padding;
    }

    setTooltipStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`
    });

    setArrowStyle({
      position: 'absolute',
      top: `${arrowTop}px`,
      left: `${arrowLeft}px`,
      display: 'block',
      '--arrow-direction': arrowDirection
    });
  }, [step]);

  // Recalculate position on resize or step change
  useEffect(() => {
    calculatePosition();
    // Recalculate after a frame so tooltipRef has the real height
    const raf = requestAnimationFrame(() => calculatePosition());
    window.addEventListener('resize', calculatePosition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [calculatePosition]);

  // Highlight target element (for all positions except plain 'center')
  useEffect(() => {
    if (!step.target || step.position === 'center') return;

    const targetElement = document.querySelector(step.target);
    if (targetElement) {
      targetElement.classList.add('onboarding-highlight');
      return () => {
        targetElement.classList.remove('onboarding-highlight');
      };
    }
  }, [step]);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setIsVisible(false);
    if (userEmail) {
      localStorage.setItem(ONBOARDING_KEY_PREFIX + userEmail, 'true');
    }
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  // Use createPortal to render directly to body, escaping any stacking context
  return createPortal(
    <>
      <div className="onboarding-overlay" />
      <div className="onboarding-tooltip" ref={tooltipRef} style={tooltipStyle}>
        <div className="onboarding-arrow" style={arrowStyle} />

        <div className="onboarding-header">
          <span className="onboarding-step-indicator">
            {currentStep + 1} / {ONBOARDING_STEPS.length}
          </span>
          <button className="onboarding-skip" onClick={handleSkip}>
            Skip tour
          </button>
        </div>

        <div className="onboarding-content">
          <h3 className="onboarding-title">{step.title}</h3>
          <p className="onboarding-message">{step.message}</p>
        </div>

        <div className="onboarding-footer">
          {!isFirstStep && (
            <button className="onboarding-btn onboarding-btn-back" onClick={handlePrev}>
              Back
            </button>
          )}
          <button className="onboarding-btn onboarding-btn-next" onClick={handleNext}>
            {isLastStep ? 'Get Started' : 'Next'}
          </button>
        </div>

        <div className="onboarding-dots">
          {ONBOARDING_STEPS.map((_, index) => (
            <span
              key={index}
              className={`onboarding-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            />
          ))}
        </div>
      </div>
    </>,
    document.body
  );
};

// Hook to check if onboarding should be shown (per-user based on email)
export const useOnboarding = (userEmail) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    if (!userEmail) {
      setIsChecking(false);
      setShowOnboarding(false);
      return;
    }
    const storageKey = ONBOARDING_KEY_PREFIX + userEmail;
    const hasSeenOnboarding = localStorage.getItem(storageKey);
    setShowOnboarding(!hasSeenOnboarding);
    setIsChecking(false);
  }, [userEmail]);

  const resetOnboarding = () => {
    if (userEmail) {
      localStorage.removeItem(ONBOARDING_KEY_PREFIX + userEmail);
    }
    setShowOnboarding(true);
  };

  const completeOnboarding = () => {
    if (userEmail) {
      localStorage.setItem(ONBOARDING_KEY_PREFIX + userEmail, 'true');
    }
    setShowOnboarding(false);
  };

  return { showOnboarding, isChecking, resetOnboarding, completeOnboarding };
};

export default Onboarding;
